import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ContentEditor from '@/components/ui/content-editor/ContentEditor';
import { ContentData, QuizData } from '@/components/ui/content-editor/types';
import { fetchLessonById, updateLesson } from '@/services/lessons';
import { toast } from 'sonner';
import { lessonService, api } from '@/services/api';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { generateUuidV7, isValidUUID } from '@/utils/uuid';
import { applyAdminViewOrdering } from '@/utils/quiz';

const LessonEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [lessonTitle, setLessonTitle] = useState('');
  const [courseName, setCourseName] = useState('');
  const [courseId, setCourseId] = useState<string | null>(null);
  const [initialContent, setInitialContent] = useState<ContentData>({ type: 'text', content: '' });
  const [initialQuiz, setInitialQuiz] = useState<QuizData>({ questions: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Redirect if not authenticated or not admin
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    const fetchLessonData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        
        // Fetch lesson data from the API
        const lessonData = await fetchLessonById(id);
        
        if (lessonData) {
          setLessonTitle(lessonData.title);
          setCourseId(lessonData.courseId);
          setInitialContent({
            type: 'text',
            content: lessonData.content || ''
          });
          
          try {
            // Fetch the course to get its name using the API service
            const courseResponse = await api.get(`/api/Course/${lessonData.courseId}`);
            const courseData = courseResponse.data as any;
            
            // Handle different possible structures
            if (courseData) {
              if (typeof courseData.title === 'string') {
                setCourseName(courseData.title);
              } else if (typeof courseData.name === 'string') {
                setCourseName(courseData.name);
              } else {
                console.warn('Course data missing title or name:', courseData);
                setCourseName(`${lessonData.courseId.substring(0, 6)}...`);
              }
            } else {
              setCourseName(`${lessonData.courseId.substring(0, 6)}...`);
            }
          } catch (courseError) {
            console.error('Error fetching course details:', courseError);
            
            // Try fetching all courses as a fallback
            try {
              const coursesResponse = await api.get('/api/Course');
              const coursesData = coursesResponse.data as any[];
              const matchingCourse = coursesData.find((c) => c.id === lessonData.courseId);
              
              if (matchingCourse) {
                if (matchingCourse.title) {
                  setCourseName(matchingCourse.title);
                } else if (matchingCourse.name) {
                  setCourseName(matchingCourse.name);
                } else {
                  setCourseName(`${lessonData.courseId.substring(0, 6)}...`);
                }
              } else {
                setCourseName(`${lessonData.courseId.substring(0, 6)}...`);
              }
            } catch (error) {
              console.error('Error fetching courses list:', error);
              setCourseName(`${lessonData.courseId.substring(0, 6)}...`);
            }
          }
          
          // Extract YouTube video ID function
          const getYouTubeVideoId = (url: string) => {
            if (!url) return null;
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
          };
          
          // Initialize quiz data if it exists
          if (lessonData.quizQuestions && lessonData.quizQuestions.length > 0) {
            console.log('Raw quiz questions from server:', lessonData.quizQuestions);
            
            // Convert server format to QuizForm format
            const formattedQuiz = {
              questions: lessonData.quizQuestions.map(q => ({
                id: q.id,
                question: q.questionText,
                options: q.answers.map(a => {
                  console.log('Answer data:', a);
                  return {
                    id: a.id || generateUuidV7(),
                    // Handle both text and answerText field names for compatibility
                    text: a.text || a.answerText || '',
                    isCorrect: a.isCorrect
                  };
                })
              }))
            };
            
            // Apply admin view ordering (sort by ID)
            const orderedQuiz = applyAdminViewOrdering(formattedQuiz);
            setInitialQuiz(orderedQuiz);
          } else {
            // Use empty quiz if no questions exist
            setInitialQuiz({ questions: [] });
          }
        } else {
          toast.error('Lesson not found');
          navigate('/admin/dashboard');
        }
      } catch (error) {
        console.error('Error loading lesson:', error);
        toast.error('Failed to load lesson data');
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && user && user.role === 'admin') {
      fetchLessonData();
    } else if (!isAuthenticated) {
      // Hook handles redirect, set loading false
       setIsLoading(false);
    }
  }, [isAuthenticated, user, navigate, id]);

  // Handle save lesson
  const handleSaveLesson = async (content: ContentData, quiz: QuizData) => {
    if (!id || !courseId) {
      toast.error('Missing lesson ID or course ID');
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Get current lesson data first to preserve any fields we're not updating
      const currentLesson = await fetchLessonById(id);
      
      if (!currentLesson) {
        throw new Error('Could not fetch current lesson data');
      }
      
      // Convert quiz data to the format expected by the API
      // Ensure all IDs are valid UUIDs
      const quizQuestions = quiz.questions.map(q => ({
        id: isValidUUID(q.id) ? q.id : generateUuidV7(),
        questionText: q.question,
        answers: q.options.map(o => ({
          id: isValidUUID(o.id) ? o.id : generateUuidV7(),
          text: o.text,
          isCorrect: o.isCorrect
        }))
      }));
      
      // Use the API service directly to ensure proper parameter passing
      const updatedLesson = await updateLesson(id, {
        title: lessonTitle,
        content: content.content,
        orderIndex: currentLesson.orderIndex || 0,
        quizQuestions: quizQuestions
      });
      
      if (!updatedLesson) {
        throw new Error('No data returned after lesson update');
      }
      
      toast.success('Lesson content and quiz saved successfully');
      
      // Navigate back to course editor
      navigate(`/admin/course/${courseId}`);
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error('Failed to save lesson content: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to={courseId ? `/admin/course/${courseId}` : "/admin/dashboard"} className="inline-flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Course
            </Link>
          </Button>
          
          <h1 className="text-3xl font-semibold tracking-tight">Edit Lesson</h1>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="ml-2">Loading lesson content...</p>
          </div>
        ) : (
          <div className="mt-6">
            <ContentEditor 
              initialContent={initialContent}
              initialQuiz={initialQuiz}
              onSave={handleSaveLesson}
              isSaving={isSaving}
              lessonTitle={lessonTitle}
              onTitleChange={setLessonTitle}
              courseName={courseName}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default LessonEditor;
